import { createContext, ReactNode, useContext, useMemo, useEffect } from 'react';
import { IntlProvider, useIntl } from 'react-intl';
import useConfig from 'hooks/useConfig';

interface TranslationProviderProps {
  children: ReactNode;
  messages: Record<string, string>;
}

const TranslationContext = createContext<{
  translate: (id: string, params?: Record<string, any>) => string;
  translateWithNewLines: (id: string) => ReactNode;
}>({
  translate: () => '',
  translateWithNewLines: () => ''
});

export const TranslationProvider = ({ children, messages }: TranslationProviderProps) => {
  const { i18n } = useConfig();

  return (
    <IntlProvider locale={i18n} defaultLocale="en" messages={messages}>
      <InternalTranslationProvider>{children}</InternalTranslationProvider>
    </IntlProvider>
  );
};

const InternalTranslationProvider = ({ children }: { children: ReactNode }) => {
  const intl = useIntl();

  const translate = useMemo(() => (id: string, values?: any) => intl.formatMessage({ id }, values), [intl]);

  const translateWithNewLines = useMemo(
    () =>
      (id: string): ReactNode => {
        const message = intl.formatMessage({ id });
        return message.split('\n').map((part, index) => (
          <span key={index}>
            {part}
            {index < message.split('\n').length - 1 && <br />}
          </span>
        ));
      },
    [intl]
  );

  useEffect(() => {
    (window as any).translate = translate;
    (window as any).translateWithNewLines = translateWithNewLines;
  }, [translate, translateWithNewLines]);

  return <TranslationContext.Provider value={{ translate, translateWithNewLines }}>{children}</TranslationContext.Provider>;
};

export const useTranslation = () => {
  return useContext(TranslationContext);
};
